import React from 'react';
import {FormattedMessage, FormattedHTMLMessage, injectIntl} from 'react-intl';

import {useFormikContext} from 'formik';

import Grid from '@mui/material/Grid';
import Tooltip from "@mui/material/Tooltip";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import Label from 'components/input/new/Label';
import InputTextField from 'components/input/new/InputTextField';
import CustomSwitch from "components/switch/CustomSwitch";

import FormBloc from './components/FormBloc';

import {greyishBlue500} from "assets/jss/main";

function BillForm({intl, isTaxRateZero}) {
    const {values, handleChange, handleBlur, setFieldValue, touched, errors} = useFormikContext();

    // Accounting fields are mandatory while the billing mandate checkbox is ticked.
    // Reading the live Formik value keeps the required markers in sync with the toggle.
    const isBillingMandate = !!values.paymentAccount?.invoiceMandateAuthorization;
    // Under the 0% VAT regime the VAT number stays optional, even with the billing mandate.
    const isVatNumberRequired = isBillingMandate && !isTaxRateZero;

    return (
        <FormBloc titleId="retailer.profile.edit.form.title.bill">
           <Grid container rowSpacing={3}>
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Label htmlFor="paymentAccount.tradeName">
                                <FormattedMessage id="retailer.profile.edit.form.field.paymentAccount.tradeName" />
                            </Label>
                            <InputTextField
                                name="paymentAccount.tradeName"
                                placeholder={intl.formatMessage({id: "retailer.profile.edit.form.field.paymentAccount.tradeName.placeholder"})}
                                value={values.paymentAccount.tradeName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.paymentAccount?.tradeName && Boolean(errors.paymentAccount?.tradeName)}
                                helperText={touched.paymentAccount?.tradeName && errors.paymentAccount?.tradeName}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Label htmlFor="paymentAccount.name" required={isBillingMandate}>
                                <FormattedMessage id="retailer.profile.edit.form.field.paymentAccount.name" />
                            </Label>
                            <InputTextField
                                name="paymentAccount.name"
                                placeholder={intl.formatMessage({id: "retailer.profile.edit.form.field.paymentAccount.name.placeholder"})}
                                value={values.paymentAccount.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.paymentAccount?.name && Boolean(errors.paymentAccount?.name)}
                                helperText={touched.paymentAccount?.name && errors.paymentAccount?.name}
                                required={isBillingMandate}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Label htmlFor="paymentAccount.siret" required={isBillingMandate}>
                                <FormattedMessage id="retailer.profile.edit.form.field.paymentAccount.siret" />
                            </Label>
                            <InputTextField
                                name="paymentAccount.siret"
                                placeholder={intl.formatMessage({id: "retailer.profile.edit.form.field.paymentAccount.siret.placeholder"})}
                                value={values.paymentAccount.siret}
                                onChange={(event) => setFieldValue('paymentAccount.siret', event.target.value.replace(/\s/g, ''))}
                                onBlur={handleBlur}
                                error={touched.paymentAccount?.siret && Boolean(errors.paymentAccount?.siret)}
                                helperText={touched.paymentAccount?.siret && errors.paymentAccount?.siret}
                                required={isBillingMandate}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Label htmlFor="paymentAccount.vatNumber" required={isVatNumberRequired}>
                                <FormattedMessage id="retailer.profile.edit.form.field.paymentAccount.vatNumber" />
                            </Label>
                            <InputTextField
                                name="paymentAccount.vatNumber"
                                placeholder={intl.formatMessage({id: "retailer.profile.edit.form.field.paymentAccount.vatNumber.placeholder"})}
                                value={values.paymentAccount.vatNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.paymentAccount?.vatNumber && Boolean(errors.paymentAccount?.vatNumber)}
                                helperText={touched.paymentAccount?.vatNumber && errors.paymentAccount?.vatNumber}
                                required={isVatNumberRequired}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Label htmlFor="paymentAccount.email">
                                <FormattedMessage id="retailer.profile.edit.form.field.paymentAccount.email" />
                            </Label>
                            <InputTextField
                                name="paymentAccount.email"
                                placeholder={intl.formatMessage({id: "retailer.profile.edit.form.field.paymentAccount.email.placeholder"})}
                                value={values.paymentAccount.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.paymentAccount?.email && Boolean(errors.paymentAccount?.email)}
                                helperText={touched.paymentAccount?.email && errors.paymentAccount?.email}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Label htmlFor="paymentAccount.contactEmail">
                                <FormattedMessage id="retailer.profile.edit.form.field.paymentAccount.contactEmail" />
                            </Label>
                            <InputTextField
                                name="paymentAccount.contactEmail"
                                placeholder={intl.formatMessage({id: "retailer.profile.edit.form.field.paymentAccount.contactEmail.placeholder"})}
                                value={values.paymentAccount.contactEmail}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.paymentAccount?.contactEmail && Boolean(errors.paymentAccount?.contactEmail)}
                                helperText={touched.paymentAccount?.contactEmail && errors.paymentAccount?.contactEmail}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Label htmlFor="paymentAccount.address" required={isBillingMandate}>
                                <FormattedMessage id="retailer.profile.edit.form.field.paymentAccount.address" />
                            </Label>
                            <InputTextField
                                name="paymentAccount.address"
                                placeholder={intl.formatMessage({id: "retailer.profile.edit.form.field.paymentAccount.address.placeholder"})}
                                value={values.paymentAccount.address}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.paymentAccount?.address && Boolean(errors.paymentAccount?.address)}
                                helperText={touched.paymentAccount?.address && errors.paymentAccount?.address}
                                required={isBillingMandate}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Label htmlFor="paymentAccount.legalRepresentative">
                                <FormattedMessage id="retailer.profile.edit.form.field.paymentAccount.legalRepresentative" />
                            </Label>
                            <InputTextField
                                name="paymentAccount.legalRepresentative"
                                placeholder={intl.formatMessage({id: "retailer.profile.edit.form.field.paymentAccount.legalRepresentative.placeholder"})}
                                value={values.paymentAccount.legalRepresentative}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.paymentAccount?.legalRepresentative && Boolean(errors.paymentAccount?.legalRepresentative)}
                                helperText={touched.paymentAccount?.legalRepresentative && errors.paymentAccount?.legalRepresentative}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Label htmlFor="paymentAccount.legalForm">
                                <FormattedMessage id="retailer.profile.edit.form.field.paymentAccount.legalForm" />
                            </Label>
                            <InputTextField
                                name="paymentAccount.legalForm"
                                placeholder={intl.formatMessage({id: "retailer.profile.edit.form.field.paymentAccount.legalForm.placeholder"})}
                                value={values.paymentAccount.legalForm}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.paymentAccount?.legalForm && Boolean(errors.paymentAccount?.legalForm)}
                                helperText={touched.paymentAccount?.legalForm && errors.paymentAccount?.legalForm}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Label htmlFor="paymentAccount.capital">
                                <FormattedMessage id="retailer.profile.edit.form.field.paymentAccount.capital" />
                            </Label>
                            <InputTextField
                                name="paymentAccount.capital"
                                placeholder={intl.formatMessage({id: "retailer.profile.edit.form.field.paymentAccount.capital.placeholder"})}
                                value={values.paymentAccount.capital}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.paymentAccount?.capital && Boolean(errors.paymentAccount?.capital)}
                                helperText={touched.paymentAccount?.capital && errors.paymentAccount?.capital}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Label htmlFor="paymentAccount.rcs">
                                <FormattedMessage id="retailer.profile.edit.form.field.paymentAccount.rcs" />
                            </Label>
                            <InputTextField
                                name="paymentAccount.rcs"
                                placeholder={intl.formatMessage({id: "retailer.profile.edit.form.field.paymentAccount.rcs.placeholder"})}
                                value={values.paymentAccount.rcs}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.paymentAccount?.rcs && Boolean(errors.paymentAccount?.rcs)}
                                helperText={touched.paymentAccount?.rcs && errors.paymentAccount?.rcs}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Label htmlFor="paymentAccount.tradeRegisterNumber">
                                <FormattedMessage id="retailer.profile.edit.form.field.paymentAccount.tradeRegisterNumber" />
                            </Label>
                            <InputTextField
                                name="paymentAccount.tradeRegisterNumber"
                                placeholder={intl.formatMessage({id: "retailer.profile.edit.form.field.paymentAccount.tradeRegisterNumber.placeholder"})}
                                value={values.paymentAccount.tradeRegisterNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.paymentAccount?.tradeRegisterNumber && Boolean(errors.paymentAccount?.tradeRegisterNumber)}
                                helperText={touched.paymentAccount?.tradeRegisterNumber && errors.paymentAccount?.tradeRegisterNumber}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Grid>
               <Grid item xs={12}>
                   <Grid container spacing={1}>
                       <Grid item xs={12}>
                           <CustomSwitch
                               onClick={handleChange}
                               name="paymentAccount.invoiceMandateAuthorization"
                               checked={values.paymentAccount?.invoiceMandateAuthorization}
                           />
                           <FormattedMessage id="retailer.profile.edit.form.field.paymentAccount.invoiceMandateAuthorization" />
                       </Grid>
                       <Grid item xs={12} sx={!values.paymentAccount?.invoiceMandateAuthorization ? {color: greyishBlue500} : undefined}>
                           <FormattedHTMLMessage id="retailer.profile.edit.form.field.paymentAccount.invoiceMandateAuthorization.description" />
                       </Grid>
                   </Grid>
               </Grid>
               <Grid item xs={12}>
                   <Grid container spacing={1}>
                       <Grid item xs={12} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                           <div>
                           <CustomSwitch
                               onClick={handleChange}
                               name="invoiceNotificationEnabled"
                               checked={values.invoiceNotificationEnabled}
                           />
                           <FormattedMessage id="retailer.profile.edit.form.field.invoiceNotificationEnabled" />
                           </div>
                           <Tooltip
                               title={<span style={{ whiteSpace: "pre-line" }}><FormattedMessage id="retailer.profile.edit.form.field.invoiceNotificationEnabled.description" /></span>}
                               arrow
                               placement="top"
                           >
                               <InfoOutlinedIcon sx={{ fontSize: 20, color: greyishBlue500, cursor: "help" }} />
                           </Tooltip>
                       </Grid>
                   </Grid>
               </Grid>
            </Grid>
        </FormBloc>
    );
}

export default injectIntl(BillForm);
